import React, { useState } from 'react';
import { Calendar, Heart, Sparkles, Users, Check, Star, ChevronDown } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../components/ui/accordion';
import BookingForm from '../components/BookingForm';
import { retreatInfo, upcomingDates, itinerary, benefits, testimonials, activities, faqs } from '../mock';
import './Home.css';

const Home = () => {
  const [showBookingForm, setShowBookingForm] = useState(false);

  const scrollToBooking = () => {
    setShowBookingForm(true);
    setTimeout(() => {
      document.getElementById('booking-section')?.scrollIntoView({ behavior: 'smooth' });
    }, 100);
  };

  const iconComponents = {
    heart: Heart,
    sparkles: Sparkles,
    users: Users
  };

  return (
    <div className="home-container">
      {/* Header */}
      <header className="site-header">
        <div className="header-content">
          <div className="logo">
            <span className="logo-text">{retreatInfo.name}</span>
          </div>
          <nav className="nav-menu">
            <a href="#about" className="nav-link">About</a>
            <a href="#program" className="nav-link">Program</a>
            <a href="#benefits" className="nav-link">Benefits</a>
            <a href="#testimonials" className="nav-link">Testimonials</a>
            <Button onClick={scrollToBooking} className="cta-button-nav">Reserve Your Spot</Button>
          </nav>
        </div>
      </header>

      {/* Hero Section */}
      <section className="hero-section">
        <div className="hero-overlay"></div>
        <div className="hero-content">
          <div className="hero-text">
            <h1 className="hero-title">
              Your Journey to<br />
              <span className="hero-accent">Softness & Renewal</span>
            </h1>
            <p className="hero-subtitle">
              Immerse yourself in {retreatInfo.duration} of holistic wellness at a pristine beachfront sanctuary in the Dominican Republic. Restore your nervous system, reconnect with your authentic self, and discover the power of community.
            </p>
            <div className="hero-cta">
              <Button onClick={scrollToBooking} size="lg" className="primary-cta">
                Reserve Your Transformation
              </Button>
              <Button variant="outline" size="lg" className="secondary-cta" onClick={() => document.getElementById('program')?.scrollIntoView({ behavior: 'smooth' })}>
                Explore the Journey
              </Button>
            </div>
          </div>
        </div>
        <div className="scroll-indicator">
          <ChevronDown className="bounce" />
        </div>
      </section>

      {/* Upcoming Dates Banner */}
      <section className="dates-banner">
        <div className="dates-content">
          <div className="dates-header">
            <Calendar className="dates-icon" />
            <h3>Upcoming Retreat Dates</h3>
          </div>
          <div className="dates-list">
            {upcomingDates.map(date => (
              <div key={date.id} className="date-card">
                <div className="date-range">{date.startDate} - {date.endDate}</div>
                <div className="spots-left">{date.spotsLeft} spots remaining</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* About Section */}
      <section id="about" className="about-section">
        <div className="container">
          <div className="section-header centered">
            <span className="section-label">The Reset Sanctuary</span>
            <h2 className="section-title">Your Beachfront Haven</h2>
            <p className="section-description">
              Nestled along the Dominican Republic's stunning coastline, The Reset Sanctuary offers pristine beaches, swaying palm trees, and crystal-clear turquoise waters. This peaceful beachfront haven provides the perfect immersive environment for healing and renewal.
            </p>
          </div>
          <div className="about-grid">
            <div className="about-image-card">
              <img src="https://customer-assets.emergentagent.com/job_luxury-escape-site/artifacts/85t651je_image.png" alt="Luxury Resort Sanctuary" className="about-image" />
            </div>
            <div className="about-image-card">
              <img src="https://customer-assets.emergentagent.com/job_luxury-escape-site/artifacts/498leo2a_image.png" alt="Infinity Pool Paradise" className="about-image" />
            </div>
          </div>
        </div>
      </section>

      {/* Program Overview */}
      <section id="program" className="program-section">
        <div className="container">
          <div className="section-header centered">
            <span className="section-label">Your Transformation Journey</span>
            <h2 className="section-title">5-Day Wellness Program</h2>
            <p className="section-description">
              Each day is thoughtfully designed to guide you deeper into restoration, self-discovery, and empowerment.
            </p>
          </div>
          <div className="itinerary-timeline">
            {itinerary.map((day, index) => (
              <div key={day.day} className="timeline-item" style={{ animationDelay: `${index * 0.1}s` }}>
                <div className="timeline-marker">
                  <span className="day-number">Day {day.day}</span>
                </div>
                <Card className="timeline-card">
                  <CardHeader>
                    <CardTitle className="day-title">{day.title}</CardTitle>
                    <CardDescription className="day-theme">{day.theme}</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <p className="day-description">{day.description}</p>
                    <div className="activities-list">
                      {day.activities.map((activity, idx) => (
                        <div key={idx} className="activity-item">
                          <Check className="activity-check" />
                          <span>{activity}</span>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Activities Grid */}
      <section className="activities-section">
        <div className="container">
          <div className="section-header centered">
            <span className="section-label">Wellness Activities</span>
            <h2 className="section-title">Heal • Move • Transform</h2>
          </div>
          <div className="activities-grid">
            <div className="activity-image-feature">
              <img src="https://customer-assets.emergentagent.com/job_luxury-escape-site/artifacts/exxyflm9_spa.png" alt="Sound Healing & Spa" className="activity-feature-img" />
              <div className="activity-overlay">
                <h3>Immersive Healing Practices</h3>
              </div>
            </div>
            <div className="activities-list-grid">
              {activities.map((activity, idx) => (
                <div key={idx} className="activity-tag">
                  <Check className="activity-icon" />
                  {activity}
                </div>
              ))}
            </div>
          </div>
          <div className="activity-images">
            <img src="https://customer-assets.emergentagent.com/job_luxury-escape-site/artifacts/4mwvh3r7_image.png" alt="Yoga by Infinity Pool" className="activity-img" />
            <img src="https://customer-assets.emergentagent.com/job_luxury-escape-site/artifacts/3nobuy4b_image.png" alt="Sound Healing Bowls" className="activity-img" />
            <img src="https://customer-assets.emergentagent.com/job_luxury-escape-site/artifacts/ssctikzo_image.png" alt="Meditation in Nature" className="activity-img" />
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section id="benefits" className="benefits-section">
        <div className="container">
          <div className="section-header centered">
            <span className="section-label">Transform Your Life</span>
            <h2 className="section-title">Benefits of The Retreat</h2>
          </div>
          <div className="benefits-grid">
            {benefits.map((benefit, idx) => {
              const IconComponent = iconComponents[benefit.icon];
              return (
                <Card key={idx} className="benefit-card">
                  <CardHeader>
                    <div className="benefit-icon">
                      <IconComponent />
                    </div>
                    <CardTitle className="benefit-title">{benefit.title}</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="benefit-description">{benefit.description}</p>
                  </CardContent>
                </Card>
              );
            })}
          </div>
          <div className="benefits-images">
            <img src="https://customer-assets.emergentagent.com/job_luxury-escape-site/artifacts/sukt16lt_image.png" alt="Group Workshop" className="benefit-img" />
            <img src="https://customer-assets.emergentagent.com/job_luxury-escape-site/artifacts/p17mtomv_yoga.png" alt="Sunset Meditation" className="benefit-img" />
            <img src="https://customer-assets.emergentagent.com/job_luxury-escape-site/artifacts/zeue1ujm_image.png" alt="Sisterhood Connection" className="benefit-img" />
          </div>
        </div>
      </section>

      {/* Nourishment Section */}
      <section className="nourishment-section">
        <div className="container">
          <div className="nourishment-grid">
            <div className="nourishment-content">
              <span className="section-label">Nourishing Meals & Moments</span>
              <h2 className="section-title">Savor • Nourish • Connect</h2>
              <p className="nourishment-description">
                Every meal is an opportunity for connection and renewal. Enjoy protein-rich breakfasts to fuel your day, leisurely lunches at local cafes, and magical sunset dinners on the beach where new friendships blossom over delicious cuisine.
              </p>
              <ul className="nourishment-list">
                <li><Check className="list-icon" /> Healthy breakfasts with green drinks</li>
                <li><Check className="list-icon" /> Fresh local cafe dining experiences</li>
                <li><Check className="list-icon" /> Sunset dinners on the beach</li>
                <li><Check className="list-icon" /> Dietary accommodations available</li>
              </ul>
            </div>
            <div className="nourishment-images">
              <img src="https://customer-assets.emergentagent.com/job_luxury-escape-site/artifacts/2y79m4dr_image%201.png" alt="Healthy Breakfast" className="nourishment-img" />
              <img src="https://customer-assets.emergentagent.com/job_luxury-escape-site/artifacts/2yq49ir2_image.png" alt="Sunset Beach Dinner" className="nourishment-img" />
            </div>
          </div>
        </div>
      </section>

      {/* Nature Excursions Section */}
      <section className="nature-section">
        <div className="container">
          <div className="nature-content-grid">
            <div className="nature-image-large">
              <img src="https://customer-assets.emergentagent.com/job_luxury-escape-site/artifacts/c6f8s4c2_image.png" alt="Cascada Waterfall" className="nature-feature-img" />
            </div>
            <div className="nature-text-content">
              <span className="section-label">Immersed in Nature</span>
              <h2 className="section-title">Connect with Mother Earth</h2>
              <p className="nature-description">
                Experience the healing power of nature with guided excursions to hidden waterfalls, pristine beaches, and lush tropical landscapes. Practice grounding exercises, eco-meditation, and forest bathing to deepen your connection with the natural world.
              </p>
              <div className="nature-highlights">
                <div className="nature-highlight-item">
                  <Check className="highlight-icon" />
                  <div>
                    <h4>Cascada Waterfall Excursion</h4>
                    <p>Swim in crystal-clear turquoise pools surrounded by jungle</p>
                  </div>
                </div>
                <div className="nature-highlight-item">
                  <Check className="highlight-icon" />
                  <div>
                    <h4>Grounding & Eco-Meditation</h4>
                    <p>Reconnect with earth energy through guided nature practices</p>
                  </div>
                </div>
                <div className="nature-highlight-item">
                  <Check className="highlight-icon" />
                  <div>
                    <h4>Beach & Forest Exploration</h4>
                    <p>Discover hidden gems along the coastline and tropical paths</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Mindfulness & Journaling Section */}
      <section className="mindfulness-journal-section">
        <div className="container">
          <div className="journal-grid">
            <div className="journal-image">
              <img src="https://customer-assets.emergentagent.com/job_luxury-escape-site/artifacts/kmwf4dbx_image.png" alt="Mindful Journaling" className="journal-img" />
            </div>
            <div className="journal-content">
              <span className="section-label">Reflection & Growth</span>
              <h2 className="section-title">Discover Your Inner Voice</h2>
              <p className="journal-description">
                Through guided journaling exercises and self-reflection practices, you'll explore your inner landscape and uncover profound insights. Each day includes dedicated time for personal reflection, goal setting, and connecting with your authentic desires.
              </p>
              <ul className="journal-benefits">
                <li><Check className="list-icon" /> Daily journaling prompts and exercises</li>
                <li><Check className="list-icon" /> Inner child healing and self-discovery</li>
                <li><Check className="list-icon" /> Life visioning and goal clarity</li>
                <li><Check className="list-icon" /> Personal transformation guidance</li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section id="testimonials" className="testimonials-section">
        <div className="container">
          <div className="section-header centered">
            <span className="section-label">Stories of Transformation</span>
            <h2 className="section-title">Hear From Our Sisters</h2>
          </div>
          <div className="testimonials-grid">
            {testimonials.map(testimonial => (
              <Card key={testimonial.id} className="testimonial-card">
                <CardContent className="testimonial-content">
                  <div className="stars">
                    {[...Array(testimonial.rating)].map((_, i) => (
                      <Star key={i} className="star-icon" fill="currentColor" />
                    ))}
                  </div>
                  <p className="testimonial-text">"{testimonial.text}"</p>
                  <div className="testimonial-author">
                    <div className="author-avatar">{testimonial.name.charAt(0)}</div>
                    <span className="author-name">{testimonial.name}</span>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="faq-section">
        <div className="container">
          <div className="section-header centered">
            <span className="section-label">Questions?</span>
            <h2 className="section-title">Frequently Asked Questions</h2>
          </div>
          <div className="faq-container">
            <Accordion type="single" collapsible className="faq-accordion">
              {faqs.map((faq, idx) => (
                <AccordionItem key={idx} value={`item-${idx}`} className="faq-item">
                  <AccordionTrigger className="faq-question">{faq.question}</AccordionTrigger>
                  <AccordionContent className="faq-answer">{faq.answer}</AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>
        </div>
      </section>

      {/* Booking Section */}
      {showBookingForm && (
        <section id="booking-section" className="booking-section">
          <div className="container">
            <div className="booking-content">
              <div className="booking-info">
                <span className="section-label">Your Renewal Awaits</span>
                <h2 className="section-title">Reserve Your Spot</h2>
                <p className="booking-description">
                  Take the first step toward healing, self-discovery, and community. Join us at The Reset Sanctuary where the ocean breeze and sisterhood will restore your spirit.
                </p>
                <div className="booking-details">
                  <div className="detail-item">
                    <Calendar className="detail-icon" />
                    <div>
                      <div className="detail-label">Duration</div>
                      <div className="detail-value">{retreatInfo.duration}</div>
                    </div>
                  </div>
                  <div className="detail-item">
                    <Heart className="detail-icon" />
                    <div>
                      <div className="detail-label">Investment</div>
                      <div className="detail-value">${retreatInfo.price.toFixed(2)} {retreatInfo.currency}</div>
                    </div>
                  </div>
                </div>
              </div>
              <BookingForm />
            </div>
          </div>
        </section>
      )}

      {/* Arrival Welcome Section */}
      <section className="arrival-section">
        <div className="container">
          <div className="arrival-content-grid">
            <div className="arrival-text">
              <span className="section-label">Begin Your Journey</span>
              <h2 className="section-title">Welcome to Paradise</h2>
              <p className="arrival-description">
                From the moment you arrive at The Reset Sanctuary, you'll be enveloped in tranquility and warmth. Walk through palm-lined paths to your beachfront haven, where crystal-clear waters and gentle breezes await. Your transformation begins the moment you step onto our sacred grounds.
              </p>
              <div className="arrival-details-list">
                <div className="arrival-detail">
                  <Heart className="arrival-icon" />
                  <div>
                    <h4>Warm Welcome Reception</h4>
                    <p>Refreshing drinks and orientation upon arrival</p>
                  </div>
                </div>
                <div className="arrival-detail">
                  <Sparkles className="arrival-icon" />
                  <div>
                    <h4>Sanctuary Tour</h4>
                    <p>Explore the grounds, beach, and wellness facilities</p>
                  </div>
                </div>
                <div className="arrival-detail">
                  <Users className="arrival-icon" />
                  <div>
                    <h4>Meet Your Sisters</h4>
                    <p>Welcome dinner and community connection</p>
                  </div>
                </div>
              </div>
            </div>
            <div className="arrival-image">
              <img src="https://customer-assets.emergentagent.com/job_luxury-escape-site/artifacts/n5gt9grl_join.png" alt="Arriving at Sanctuary" className="arrival-feature-img" />
            </div>
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="final-cta-section">
        <div className="final-cta-content">
          <h2 className="final-cta-title">Ready to Transform Your Life?</h2>
          <p className="final-cta-text">
            Limited spots available. Reserve your place at The Reset Sanctuary today.
          </p>
          <Button onClick={scrollToBooking} size="lg" className="final-cta-button">
            Reserve Your Transformation Now
          </Button>
        </div>
      </section>

      {/* Footer */}
      <footer className="site-footer">
        <div className="footer-content">
          <div className="footer-section">
            <h3 className="footer-title">{retreatInfo.name}</h3>
            <p className="footer-tagline">{retreatInfo.tagline}</p>
            <p className="footer-text">{retreatInfo.location}</p>
          </div>
          <div className="footer-section">
            <h4 className="footer-heading">Contact Us</h4>
            <p className="footer-text">{retreatInfo.email}</p>
          </div>
          <div className="footer-section">
            <h4 className="footer-heading">Follow Your Journey</h4>
            <p className="footer-text">Connect with us on social media</p>
          </div>
        </div>
        <div className="footer-bottom">
          <p>&copy; 2025 The Escape Retreat. All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
};

export default Home;